import { useCallback, useEffect, useState } from 'react';
import { VOLATILITY_LEVELS, type VolatilityLevel } from '../../../modules/api';

// One JSON blob `{ [poolOfferId]: level }` so each pack remembers its own
// volatility across reloads (pack A on Max, pack B on Normal).
const STORAGE_KEY = 'gp:pack-volatility';

function isLevel(value: unknown): value is VolatilityLevel {
  return typeof value === 'string' && (VOLATILITY_LEVELS as string[]).includes(value);
}

function readStore(): Record<string, VolatilityLevel> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, VolatilityLevel> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (isLevel(v)) out[k] = v;
    }
    return out;
  } catch {
    // Malformed JSON or storage blocked (sandboxed iframe) — start fresh.
    return {};
  }
}

function writeStore(store: Record<string, VolatilityLevel>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Storage unavailable / quota exceeded — persistence is best-effort.
  }
}

/**
 * Per-pack volatility level, persisted to localStorage keyed by pool_offer id.
 * Reloading the page — or switching the active pack in the slider — restores
 * the level that pack was last played at (defaults to `'normal'`). Selections
 * for different packs are independent.
 */
export function usePackVolatility(
  offerId: string | number | null | undefined,
): [VolatilityLevel, (level: VolatilityLevel) => void] {
  const key = offerId != null ? String(offerId) : '';
  const [volatility, setVolatilityState] = useState<VolatilityLevel>(
    () => readStore()[key] ?? 'normal',
  );

  // Re-hydrate when the active pack changes (slider swipe / category switch).
  useEffect(() => {
    setVolatilityState(readStore()[key] ?? 'normal');
  }, [key]);

  const setVolatility = useCallback(
    (level: VolatilityLevel) => {
      setVolatilityState(level);
      if (!key) return;
      const store = readStore();
      store[key] = level;
      writeStore(store);
    },
    [key],
  );

  return [volatility, setVolatility];
}
